import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { isString } from "../app/utils";
import {
	nullAccessibility,
	setAccessibility,
	setDistrict,
	setSchool,
	setUser,
	UserRole,
} from "../app/userSlice";
import { siteApiSlice } from "../app/siteApiSlice";
import Footer from "../features/footer/Footer";
import Header from "../features/header/Header";
import { useGetSelfMutation } from "../features/users/usersApiSlice";
import { useMakeDistrictSelectionMutation } from "../features/districts/districtsApiSlice";
import { useMakeSchoolSelectionMutation } from "../features/schools/schoolsApiSlice";
import { applyAccessibility } from "../features/accessibility/accessibilityUtil";

export default function Private(props: any) {
	const {
		isLoggedIn,
		userId: loggedInUserId,
		isTeachersWriterAccount,
		requiresAccountConfirmation,
		bypassTermsOfService,
		isImpersonating,
	} = useAppSelector((state) => state.user);
	const [userId] = useState(loggedInUserId);

	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const location = useLocation();

	const [callGetSelf] = useGetSelfMutation();
	const [makeDistrictSelection] = useMakeDistrictSelectionMutation();
	const [makeSchoolSelection] = useMakeSchoolSelectionMutation();

	useEffect(() => {
		callGetSelf()
			.unwrap()
			.then((payload) => {
				dispatch(setUser({ ...payload }));
				dispatch(
					setAccessibility(
						payload.accessibility
							? { ...payload.accessibility }
							: { ...nullAccessibility }
					)
				);
				dispatch(setSchool(payload.school));
				dispatch(setDistrict(payload.district));
				applyAccessibility(payload.accessibility);

				// Keep the server-side session's selected district/school in sync with
				// what GetSelf resolved (which falls back to the admin's only
				// district/school when one hasn't been explicitly selected). Without
				// this, admins with a single district/school can end up with the SPA
				// showing a district/school selected while the server-side session
				// still has none selected, causing endpoints that rely on
				// _userSession.District/School (e.g. report access checks) to fail.
				if (payload.role === UserRole.DistrictAdmin && payload.district) {
					makeDistrictSelection(payload.district.id)
						.unwrap()
						.catch((error: any) => {
							const e = isString(error) ? error : JSON.stringify(error);
							console.log(`error calling MakeDistrictSelection, error = ${e}`);
						});
				}
				if (payload.role === UserRole.SchoolAdmin && payload.school) {
					makeSchoolSelection(payload.school.id)
						.unwrap()
						.catch((error: any) => {
							const e = isString(error) ? error : JSON.stringify(error);
							console.log(`error calling MakeSchoolSelection, error = ${e}`);
						});
				}
			})
			.catch((error: any) => {
				const e = isString(error) ? error : JSON.stringify(error);
				console.log(`error calling GetSelf, error = ${e}`);
			});
	}, []);

	useEffect(() => {
		if (!isLoggedIn || !loggedInUserId || loggedInUserId !== userId) {
			dispatch(siteApiSlice.util.resetApiState());
			if (!isTeachersWriterAccount || !isLoggedIn) navigate("/login");
		}
	}, [isLoggedIn, loggedInUserId]);

	useEffect(() => {
		if (!isImpersonating) {
			if (!bypassTermsOfService) {
				navigate("/termsOfService");
			} else if (requiresAccountConfirmation) {
				navigate("/myAccount");
			}
		}
	}, [location.pathname]);

	return (
		<div className="flex flex-col min-h-screen bg-white">
			<Header />
			<main className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-8 py-8 w-screen">
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
